/**
 * Two-tier prefix cache for username search (plan §3.4).
 *
 *   tier 1  in-process LRU        60 s, ≤ 500 entries (hottest prefixes, zero network)
 *   tier 2  shared Redis          rs:{platform}:u:{prefix lowercased}
 *             fresh    ≤ 24 h     served as is
 *             stale    ≤ 7 d      served at once (physical TTL), revalidated in the background
 *             negative 5 min      rs:…:neg — "this prefix has no results", short-circuits
 *   legacy  ig_query_users_* / tt_query_users_* stay READABLE (their own TTL = their freshness)
 *           and are dual-written (6 h) so flipping SOCIAL_ROUTER_ENABLED off finds a warm cache.
 *
 * On a total router failure the answer degrades to whatever stale entry exists (any age,
 * including a legacy key); only an empty cache throws. Entries carry `t` (fetchedAt); serving a
 * stale entry never renews it, an LRU copy never outlives the entry's remaining fresh window, and a
 * stale entry is not parked in the LRU (every request keeps nudging the single refresher instead).
 * One live lookup per key: in-process coalescing plus a short Redis lock (`…:flight`) so a second
 * isolate waits for the winner's cache write instead of fanning out to the providers.
 *
 * 🔴 Browser+edge-safe: shared Upstash client only. Every Redis error degrades to a miss.
 */
import { redisClient } from '../redis';
import { bounded, fireAndForget, runInBackground, type Platform } from './registry';

export const FRESH_MS = 24 * 60 * 60 * 1000;
export const STALE_TTL_S = 7 * 24 * 60 * 60;
export const NEGATIVE_TTL_S = 5 * 60;
export const LRU_TTL_MS = 60_000;
export const LRU_MAX = 500;
const LEGACY_TTL_S = 6 * 60 * 60;
const REVALIDATE_LOCK_S = 20;
const FLIGHT_LOCK_S = 5;
const FLIGHT_POLL_MS = 150;
const FLIGHT_WAIT_POLLS = 3;
/** How long a cache write may take; it runs past the response (waitUntil), so it never delays the visitor.
 *  The Redis lives in us1 while most traffic is served from sin1, where a write takes 300-700 ms: at 400 ms every
 *  write was dropped and nothing ever landed in the cache, so each lookup stayed cold. */
const WRITE_WAIT_MS = 2_000;
/** Redis reads are on the visitor's path and may only cost this much before the answer goes live. */
const READ_WAIT_MS = 700;

export type CacheSource = 'lru' | 'prefix' | 'fresh' | 'legacy' | 'negative' | 'stale' | 'live' | 'stale-on-error';

/** `fetchedAt` = when the fetch that produced `v` STARTED (ms epoch). Only a live fetch sets it; serving
 *  a stale entry never touches it, and a write never replaces an entry with a larger fetchedAt. */
export interface Entry {
	v: unknown[];
	fetchedAt: number;
}

export const searchCacheKey = (platform: Platform, query: string): string => `rs:${platform}:u:${query.trim().toLowerCase()}`;

// ── in-process LRU ───────────────────────────────────────────────────────────────────
const lru = new Map<string, { v: unknown[]; at: number }>();

export function lruGet(key: string, now = Date.now()): unknown[] | null {
	const hit = lru.get(key);
	if (!hit) return null;
	if (now - hit.at > LRU_TTL_MS) {
		lru.delete(key);
		return null;
	}
	lru.delete(key); // refresh recency
	lru.set(key, hit);
	return hit.v;
}

/** `ttlMs` caps the copy's lifetime (default 60 s) — never beyond what the Redis entry still allows. */
export function lruSet(key: string, v: unknown[], now = Date.now(), ttlMs = LRU_TTL_MS): void {
	lru.delete(key);
	// store the copy as if it had been written (LRU_TTL_MS - ttlMs) ago so lruGet's single TTL check expires it on time
	lru.set(key, { v, at: now - (LRU_TTL_MS - Math.min(ttlMs, LRU_TTL_MS)) });
	while (lru.size > LRU_MAX) {
		const oldest = lru.keys().next().value;
		if (oldest === undefined) break;
		lru.delete(oldest);
	}
}

export function __lruSize(): number {
	return lru.size;
}
export function __lruClear(): void {
	lru.clear();
}

// ── helpers ──────────────────────────────────────────────────────────────────────────
function asEntry(raw: unknown): Entry | null {
	try {
		const v: any = typeof raw === 'string' ? JSON.parse(raw) : raw;
		if (v && Array.isArray(v.v) && typeof v.fetchedAt === 'number') return v as Entry;
	} catch { /* corrupt */ }
	return null;
}

/** fetchedAt of a negative entry, or null. */
function asNeg(raw: unknown): number | null {
	try {
		const v: any = typeof raw === 'string' ? JSON.parse(raw) : raw;
		return v && typeof v.fetchedAt === 'number' ? v.fetchedAt : null;
	} catch {
		return null;
	}
}

/** A legacy cache value is a plain JSON array (the negative sentinel is a string and is ignored). */
function asLegacy(raw: unknown): unknown[] | null {
	try {
		const v: any = typeof raw === 'string' ? JSON.parse(raw) : raw;
		return Array.isArray(v) && v.length ? v : null;
	} catch {
		return null;
	}
}

const inflight = new Map<string, Promise<unknown[]>>();

// Atomic compare-and-set: refuse to overwrite an entry whose fetchedAt is newer than ours.
const WRITE_IF_NEWER_LUA = `
local cur = redis.call('GET', KEYS[1])
if cur then
  local ok, o = pcall(cjson.decode, cur)
  if ok and type(o) == 'table' and tonumber(o.fetchedAt) and tonumber(o.fetchedAt) > tonumber(ARGV[2]) then return 0 end
end
redis.call('SET', KEYS[1], ARGV[1], 'EX', tonumber(ARGV[3]))
return 1`;

/** Write `entry` unless the stored one is newer. Returns true when written. Throws on Redis failure. */
export async function writeIfNewer(key: string, entry: Entry, ttlS: number): Promise<boolean> {
	const payload = JSON.stringify(entry);
	try {
		return Number(await redisClient.eval(WRITE_IF_NEWER_LUA, [key], [payload, String(entry.fetchedAt), String(ttlS)])) === 1;
	} catch (error: any) {
		// EVAL unavailable: read-compare-write (small race window, still never regresses a settled entry)
		if (!/script|eval|ERR unknown command|NOSCRIPT/i.test(String(error?.message))) throw error;
		const cur = asEntry(await redisClient.get(key));
		if (cur && cur.fetchedAt > entry.fetchedAt) return false;
		await redisClient.set(key, payload, { ex: ttlS });
		return true;
	}
}

async function store(key: string, legacyKey: string, data: unknown[], fetchedAt: number): Promise<void> {
	try {
		if (data.length) {
			await bounded(Promise.all([
				writeIfNewer(key, { v: data, fetchedAt }, STALE_TTL_S),
				(async () => redisClient.set(legacyKey, JSON.stringify(data), { ex: LEGACY_TTL_S }))(), // async wrapper: a sync client error must not orphan the CAS promise
			]), WRITE_WAIT_MS);
		} else {
			// a confirmed "no results" lives 5 minutes
			await bounded(redisClient.set(`${key}:neg`, JSON.stringify({ fetchedAt }), { ex: NEGATIVE_TTL_S }), WRITE_WAIT_MS);
		}
	} catch (error: any) {
		console.error('[router/cache] write failed (served anyway):', error?.message || error);
	}
}

/** Up to two shorter prefixes of the typed query (never below 2 chars), longest first. */
export function prefixKeys(platform: Platform, query: string): string[] {
	const q = query.trim().toLowerCase();
	const out: string[] = [];
	for (let cut = 1; cut <= 2 && q.length - cut >= 2; cut++) out.push(searchCacheKey(platform, q.slice(0, q.length - cut)));
	return out;
}

/** Rows whose handle or display name still contains the longer query (the prefix rows are a superset of the answer). */
export function narrowRows(rows: unknown[], query: string): unknown[] {
	const needle = query.trim().toLowerCase();
	if (!needle) return [];
	return rows.filter((r) => {
		const row = r as Record<string, unknown>;
		const handle = String(row?.username ?? row?.unique_id ?? '').toLowerCase();
		const name = String(row?.full_name ?? row?.nickname ?? '').toLowerCase();
		return handle.includes(needle) || name.includes(needle);
	});
}

export interface CachedSearchOptions {
	platform: Platform;
	query: string;
	/** Old-format key (ig_query_users_<q> / tt_query_users_<q>): read as a fallback, dual-written. */
	legacyKey: string;
	/** Skip every cache READ (internal health checks); the result is still written. */
	ignoreCache?: boolean;
	/** Live lookup through the router. Throws when every provider failed. */
	fetchLive: () => Promise<unknown[]>;
}

/** Cache-through username search. Returns the rows plus where they came from. */
export async function cachedSearch(o: CachedSearchOptions): Promise<{ data: unknown[]; source: CacheSource }> {
	const key = searchCacheKey(o.platform, o.query);
	const now = Date.now();
	let entry: Entry | null = null;
	let legacy: unknown[] | null = null;
	let read = false;

	if (!o.ignoreCache) {
		const hit = lruGet(key, now);
		if (hit) return { data: hit, source: 'lru' };

		// Typing "nik" → "nike" mints a new key every keystroke, so without this every keystroke paid a full provider lap.
		// The shorter prefix's rows are still in the isolate LRU: keep those that match the longer query and answer at once.
		for (const shorter of prefixKeys(o.platform, o.query)) {
			const rows = lruGet(shorter, now);
			const matched = rows ? narrowRows(rows, o.query) : [];
			if (matched.length) return { data: matched, source: 'prefix' };
		}

		try {
			const [rawEntry, rawNeg, rawLegacy] = (await bounded(redisClient.mget(key, `${key}:neg`, o.legacyKey), READ_WAIT_MS)) as unknown[];
			read = true;
			entry = asEntry(rawEntry);
			legacy = asLegacy(rawLegacy);
			if (rawNeg && !entry && !legacy) {
				lruSet(key, [], now);
				return { data: [], source: 'negative' };
			}
			if (entry && now - entry.fetchedAt < FRESH_MS) {
				lruSet(key, entry.v, now, FRESH_MS - (now - entry.fetchedAt));
				return { data: entry.v, source: 'fresh' };
			}
			if (legacy) {
				lruSet(key, legacy, now);
				return { data: legacy, source: 'legacy' };
			}
			if (entry) {
				// stale (≤ 7 d by physical TTL): answer now, refresh behind the response (not parked in the LRU)
				runInBackground(revalidate(key, o, entry.fetchedAt));
				return { data: entry.v, source: 'stale' };
			}
		} catch (error: any) {
			console.error('[router/cache] read failed (treated as miss):', error?.message || error);
		}
	}

	try {
		const data = await live(key, o, entry?.fetchedAt);
		return { data, source: 'live' };
	} catch (error) {
		// Total failure: the stalest answer beats an error.
		if (!read) {
			try {
				const [rawEntry, , rawLegacy] = (await bounded(redisClient.mget(key, `${key}:neg`, o.legacyKey), READ_WAIT_MS)) as unknown[];
				entry = asEntry(rawEntry);
				legacy = asLegacy(rawLegacy);
			} catch { /* nothing to fall back to */ }
		}
		const stale = entry?.v ?? legacy;
		if (stale) {
			console.warn(`[router/cache] ${key}: all providers failed, serving stale answer (${stale.length} rows)`);
			return { data: stale, source: 'stale-on-error' };
		}
		throw error;
	}
}

/** One live lookup per key at a time within this isolate. */
function live(key: string, o: CachedSearchOptions, seenFetchedAt?: number): Promise<unknown[]> {
	const existing = inflight.get(key);
	if (existing) return existing;
	const p = (async () => {
		// Another isolate already fetching this key? Wait briefly for its cache write instead of fanning out.
		const startedAt = Date.now();
		// What counts as "the winner's result": anything fetched after the entry WE already saw (a stale one), or, when we
		// read nothing (first miss / ignoreCache), anything fetched since the lock could have been taken (its TTL ago).
		const newerThan = seenFetchedAt ?? startedAt - FLIGHT_LOCK_S * 1000;
		let owner = true;
		try {
			owner = !!(await bounded(redisClient.set(`${key}:flight`, '1', { nx: true, ex: FLIGHT_LOCK_S }), 150));
		} catch { /* Redis down: fetch ourselves */ }
		if (!owner) {
			for (let i = 0; i < FLIGHT_WAIT_POLLS; i++) {
				await new Promise((r) => setTimeout(r, FLIGHT_POLL_MS));
				try {
					const [rawEntry, rawNeg] = (await bounded(redisClient.mget(key, `${key}:neg`), 150)) as unknown[];
					const winner = asEntry(rawEntry);
					if (winner && winner.fetchedAt > newerThan) {
						lruSet(key, winner.v);
						return winner.v;
					}
					const neg = asNeg(rawNeg);
					if (neg !== null && neg > newerThan) { // the winner confirmed "no results"
						lruSet(key, []);
						return [];
					}
				} catch { /* keep waiting / fall through */ }
			}
		}
		try {
			const data = await o.fetchLive();
			lruSet(key, data);
			runInBackground(store(key, o.legacyKey, data, startedAt));
			return data;
		} finally {
			if (owner) fireAndForget('flight unlock', () => redisClient.del(`${key}:flight`));
		}
	})();
	inflight.set(key, p);
	const clear = () => { inflight.delete(key); };
	p.then(clear, clear);
	return p;
}

async function revalidate(key: string, o: CachedSearchOptions, seenFetchedAt?: number): Promise<void> {
	try {
		// one refresher across isolates
		const lock = await bounded(redisClient.set(`${key}:lock`, '1', { nx: true, ex: REVALIDATE_LOCK_S }));
		if (!lock) return;
	} catch { /* Redis down: skip the refresh, the stale answer was already served */ return; }
	await live(key, o, seenFetchedAt);
}
